import React, { useState } from "react";
import axios from "axios";
import { FaFilePdf } from "react-icons/fa6";
import { ReceiveTransactionProps } from "../../types/order";

type Props = {
  transactions: ReceiveTransactionProps[];
  isInSet: boolean;
};

const ReceiveHistoryList = ({ transactions, isInSet }: Props) => {
  const [loadingPdfId, setLoadingPdfId] = useState<number | null>(null);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = (hours % 12 || 12).toString().padStart(2, '0');
    return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
  };

  const handleViewPdf = async (transactionId: number) => {
    setLoadingPdfId(transactionId);

    try {
      const token = localStorage.getItem('authToken');
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';

      const response = await axios.get(
        `${apiBaseUrl}/v1/worker/receive-transactions/${transactionId}/pdf`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'apikey': process.env.REACT_APP_API_KEY,
          },
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');

      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (error) {
      console.error('Error fetching PDF:', error);
      alert('PDF લોડ કરવામાં ભૂલ થઈ. કૃપા કરીને ફરીથી પ્રયત્ન કરો.');
    }

    setLoadingPdfId(null);
  };

  if (!transactions || transactions.length === 0) {
    return null;
  }

  return (
    <div className="mt-4">
      {/* Header */}
      <div className="bg-white rounded-t-2xl shadow-lg px-4 py-3 border-b border-gray-100">
        <h3 className="text-lg font-bold text-primary-1 text-center">
          ડિલિવરી હિસ્ટ્રી ({transactions.length})
        </h3>
      </div>

      {/* Transaction List */}
      <div className="bg-white rounded-b-2xl shadow-lg overflow-hidden">
        {transactions.map((tx, index) => (
          <div
            key={tx.id}
            className={`p-4 ${index !== transactions.length - 1 ? 'border-b border-gray-100' : ''}`}
          >
            {/* Header Row */}
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center gap-2">
                <span className="bg-primary-1 text-white text-sm font-bold px-2.5 py-1 rounded-full">
                  #{transactions.length - index}
                </span>
                <span className="text-gray-500 text-sm">
                  {formatDate(tx.created_at)}
                </span>
              </div>
              <button
                onClick={() => handleViewPdf(tx.id)}
                disabled={loadingPdfId === tx.id}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm font-medium disabled:opacity-50"
              >
                <FaFilePdf className="text-sm" />
                {loadingPdfId === tx.id ? 'લોડ...' : 'PDF'}
              </button>
            </div>

            {/* Details Grid */}
            <div className="bg-gray-50 rounded-xl p-3">
              {isInSet ? (
                <div className="space-y-2">
                  {/* Kesh Details (if present) */}
                  {(tx.qty_field_2 !== null && tx.qty_field_2 > 0) && (
                    <div className={`grid grid-cols-2 gap-2 ${tx.qty_field_1 > 0 ? 'pt-2 border-t border-gray-200' : ''}`}>
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">કેશ નંગ:</span>
                        <span className="text-primary-1 font-semibold text-sm">{tx.qty_field_2 || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">કેશ વજન:</span>
                        <span className="text-primary-1 font-semibold text-sm">{(tx.weight_field_2 || 0).toFixed(3)} કિ.</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Patta Details (if present) */}
                  {(tx.qty_field_1 > 0) && (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">પટ્ટા નંગ:</span>
                        <span className="text-primary-1 font-semibold text-sm">{tx.qty_field_1}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 text-sm">પટ્ટા વજન:</span>
                        <span className="text-primary-1 font-semibold text-sm">{tx.weight_field_1.toFixed(3)} કિ.</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500 text-sm">નંગ:</span>
                    <span className="text-primary-1 font-semibold text-sm">{tx.qty_field_1}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 text-sm">વજન:</span>
                    <span className="text-primary-1 font-semibold text-sm">{tx.weight_field_1.toFixed(3)} કિલો</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReceiveHistoryList;
